import { useEffect, type ReactNode } from 'react'
import './Modal.css'

interface Props {
  title: string
  subtitle?: string
  onClose: () => void
  children: ReactNode
  footer: ReactNode
  width?: number
  /**
   * How the dialog may be dismissed.
   *
   * `anywhere` is the default and what every dialog in this app has always
   * done: Escape, or a click on the backdrop. Right for a dialog that shows
   * something, and wrong for one somebody has spent ten minutes typing into —
   * a stray click outside a half-written letter to the shareholders throws it
   * away with no warning and no undo.
   *
   * `explicit` closes only on the ✕ in the corner. It is opt-in rather than the
   * default because the escape hatch is worth keeping wherever nothing is at
   * stake, and because a dialog that ignores Escape had better be showing the
   * user the button that does work.
   */
  dismiss?: 'anywhere' | 'explicit'
  /**
   * An extra class on the dialog itself.
   *
   * For the one thing a dialog cannot style from the inside: its own height.
   * A pane that scrolls independently needs every ancestor up to the dialog to
   * have a bounded height, and only the dialog can be told what that is.
   */
  className?: string
}

/**
 * Centred dialog over a navy scrim, per the design's modal spec.
 *
 * Escape closes and a click on the backdrop closes; a click inside does not.
 * Body scroll is locked while open, otherwise the page behind scrolls under the
 * dialog on a trackpad.
 *
 * A dialog holding unsaved work can refuse both — see `dismiss`.
 */
export function Modal({
  title,
  subtitle,
  onClose,
  children,
  footer,
  width = 440,
  dismiss = 'anywhere',
  className = '',
}: Props) {
  const explicit = dismiss === 'explicit'

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !explicit) onClose()
    }
    document.addEventListener('keydown', onKey)
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previous
    }
  }, [onClose, explicit])

  return (
    <div
      className="modal__scrim"
      onClick={explicit ? undefined : onClose}
      role="presentation"
    >
      <div
        className={`modal${explicit ? ' modal--explicit' : ''}${className ? ` ${className}` : ''}`}
        style={{ width }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {explicit && (
          <button className="modal__x" onClick={onClose} aria-label="Close" title="Close">
            ✕
          </button>
        )}
        <div className="modal__title">{title}</div>
        {subtitle && <div className="modal__subtitle">{subtitle}</div>}
        <div className="modal__body">{children}</div>
        <div className="modal__footer">{footer}</div>
      </div>
    </div>
  )
}
